import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AppLayout } from '../components/AppLayout';
import { Switch } from '../components/ui/switch';
import { PageHeader } from '../components/ds';
import { User, Mail, Shield, Globe, LogOut, Moon, Sun } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const [darkMode, setDarkMode] = React.useState(
    document.documentElement.classList.contains('dark')
  );
  const [confirmLogout, setConfirmLogout] = React.useState(false);

  const handleDarkModeToggle = () => {
    document.documentElement.classList.toggle('dark');
    setDarkMode(!darkMode);
    localStorage.setItem('theme', !darkMode ? 'dark' : 'light');
  };

  const dateLocale = language === 'fr' ? fr : enUS;
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
        <PageHeader overline={language === 'fr' ? 'Compte · Préférences personnelles' : 'Account · Personal preferences'} title={t('profile')} />

        {/* Informations du compte */}
        <div className="rounded-card bg-surface p-5 sm:p-6">
          <h2 className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg mb-5 flex items-center gap-2">
            <User className="w-5 h-5 text-text-2" />
            {language === 'fr' ? 'Informations du compte' : 'Account Information'}
          </h2>
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 rounded-full bg-orange flex items-center justify-center">
              <span className="font-display text-2xl font-bold text-orange-on">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">{user?.name}</h3>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-[6px] text-xs font-bold ${isAdmin ? 'bg-graphite-900 text-white' : 'bg-surface-2 text-[#2E2D2A]'}`}>{user?.role}</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-ctrl bg-surface-2">
              <Mail className="w-5 h-5 text-text-3" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-label">{t('email')}</p>
                <p className="font-medium text-ink">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-ctrl bg-surface-2">
              <Shield className="w-5 h-5 text-text-3" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-label">{t('role')}</p>
                <p className="font-medium text-ink capitalize">{user?.role}</p>
              </div>
            </div>
            {user?.created_at && (
              <div className="flex items-center gap-3 p-3 rounded-ctrl bg-surface-2">
                <User className="w-5 h-5 text-text-3" />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-label">{t('createdAt')}</p>
                  <p className="font-medium text-ink">{format(new Date(user.created_at), 'PPP', { locale: dateLocale })}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Préférences */}
        <div className="rounded-card bg-surface p-5 sm:p-6">
          <h2 className="font-display font-bold uppercase tracking-[0.02em] text-ink text-lg mb-5">{language === 'fr' ? 'Préférences' : 'Preferences'}</h2>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-text-3" />
                <div>
                  <p className="text-base font-medium text-ink">{language === 'fr' ? 'Langue' : 'Language'}</p>
                  <p className="text-sm text-text-3">{language === 'fr' ? 'Français' : 'English'}</p>
                </div>
              </div>
              <button onClick={toggleLanguage} className="h-10 px-4 rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink hover:bg-surface-2 transition-colors" data-testid="language-toggle">
                {language === 'fr' ? 'EN' : 'FR'}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {darkMode ? <Moon className="w-5 h-5 text-text-3" /> : <Sun className="w-5 h-5 text-text-3" />}
                <div>
                  <p className="text-base font-medium text-ink">{language === 'fr' ? 'Mode sombre' : 'Dark Mode'}</p>
                  <p className="text-sm text-text-3">{darkMode ? (language === 'fr' ? 'Activé' : 'Enabled') : (language === 'fr' ? 'Désactivé' : 'Disabled')}</p>
                </div>
              </div>
              <Switch id="dark-mode" checked={darkMode} onCheckedChange={handleDarkModeToggle} data-testid="dark-mode-toggle" />
            </div>
          </div>
        </div>

        {/* Déconnexion — bouton secondaire texte danger + confirmation §6.9 */}
        <div className="rounded-card bg-surface p-5">
          {!confirmLogout ? (
            <button onClick={() => setConfirmLogout(true)} className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-ctrl text-sm font-semibold bg-surface border border-border text-danger hover:bg-surface-2 transition-colors" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
              {t('logout')}
            </button>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="logout-confirm">
              <span className="text-sm text-text-2">{language === 'fr' ? 'Confirmer la déconnexion ?' : 'Confirm logout?'}</span>
              <div className="flex gap-2">
                <button onClick={() => setConfirmLogout(false)} className="h-10 px-4 rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink hover:bg-surface-2">{language === 'fr' ? 'Annuler' : 'Cancel'}</button>
                <button onClick={logout} className="h-10 px-4 rounded-ctrl text-sm font-bold text-white" style={{ background: '#B42318' }} data-testid="logout-confirm-btn">{t('logout')}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
